import { useEffect, useMemo, useState } from 'react';
import ApplicationModel from '../../../../../../shared/models/applications/api/ApplicationModel';
import MedicalIntegrationConfigModel from '../../../../../../shared/models/organizations/MedicalIntegrationConfigModel';
import { ModuleFieldLocation, findModuleField } from '../../../../../../shared/utils/moduleFieldFinalize';
import { isSandboxMedicalIntegrationConfigId } from '../dentalSandboxWarnings';

export const CONFIG_ID_VARIABLE = '{MedicalIntegrationConfigId}';
// Location reads run a few at a time so large orgs don't flood the API
const LOAD_BATCH_SIZE = 5;

export interface ConfigIdFieldRow {
    app: ApplicationModel;
    isShared: boolean;
    // Null when this app has no field of its own (locations then use the Shared Configuration's value)
    field: ModuleFieldLocation | null;
    error?: string;
}

export type ConnectionStatus = 'set' | 'not-set' | 'sandbox' | 'unknown';

export const sameConfigId = (a?: string | null, b?: string | null) => !!a && !!b && a.trim().toLowerCase() === b.trim().toLowerCase();

export const getConnectionStatus = (value: string | null | undefined, configs: MedicalIntegrationConfigModel[]): ConnectionStatus => {
    if (!value?.trim()) return 'not-set';
    if (configs.some(config => sameConfigId(config.id, value))) return 'set';
    if (isSandboxMedicalIntegrationConfigId(value)) return 'sandbox';
    return 'unknown';
};

// A location still needs connecting when its field is empty or still points at the sandbox configuration
export const needsConnection = (rows: ConfigIdFieldRow[], configs: MedicalIntegrationConfigModel[]): boolean => {
    const status = (row: ConfigIdFieldRow) => getConnectionStatus(row.field?.value, configs);
    return rows.some(row => row.field && (status(row) === 'not-set' || status(row) === 'sandbox'));
};

/**
 * Reads the Medical Integration Configuration ID field from the Shared Configuration and every location.
 * This is one module request per app, the same request the Location Manager makes for each location.
 */
export const useConfigIdFieldRows = (locationApps: ApplicationModel[]) => {
    const apps = useMemo(() => [
        ...locationApps.filter(app => app.allowsTemplating),
        ...locationApps.filter(app => !app.allowsTemplating).sort((a, b) => (a.name || '').localeCompare(b.name || ''))
    // Keyed on IDs: the parent rebuilds this list each render, and a new array would re-read every location
    ], [locationApps.map(app => app.id).join(',')]);

    const [rows, setRows] = useState<ConfigIdFieldRow[]>([]);
    const [isLoading, setIsLoading] = useState(apps.length > 0);
    const [hasLoaded, setHasLoaded] = useState(apps.length === 0);

    useEffect(() => {
        let cancelled = false;
        const load = async () => {
            setIsLoading(true);
            const loaded: ConfigIdFieldRow[] = [];
            for (let i = 0; i < apps.length; i += LOAD_BATCH_SIZE) {
                const batch = await Promise.all(apps.slice(i, i + LOAD_BATCH_SIZE).map(async app => {
                    const result = await findModuleField(app.id, CONFIG_ID_VARIABLE);
                    return {
                        app,
                        isShared: !!app.allowsTemplating,
                        field: result.resultType === 'Ok' ? result.data : null,
                        error: result.resultType === 'Ok' ? undefined : (result.errors?.join(', ') || "Couldn't read modules")
                    } as ConfigIdFieldRow;
                }));
                if (cancelled) return;
                loaded.push(...batch);
            }
            setRows(loaded);
            setIsLoading(false);
            setHasLoaded(true);
        };
        if (apps.length > 0) {
            load();
        } else {
            setRows([]);
            setIsLoading(false);
            setHasLoaded(true);
        }
        return () => { cancelled = true; };
    }, [apps]);

    const setRowValue = (appId: string, value: string) => {
        setRows(current => current.map(row => row.app.id === appId && row.field ? { ...row, field: { ...row.field, value } } : row));
    };

    return { rows, isLoading, hasLoaded, setRowValue };
};
